/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import config from 'linced-app/config/environment';
import NswSapSyncStatus from 'linced-app/enums/nsw-sap-sync-status';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import moment from 'moment';

export default class IndexController extends Controller {
  @service
  router;

  @action
  setLedgerItemDefaultAmount(value) {
    this.ledgerItemDefaultAmount = value;
  }

  @action
  setLedgerItemDefaultDate(value) {
    this.ledgerItemDefaultDate = value;
  }

  @action
  setLedgerItemDefaultDescription(value) {
    this.ledgerItemDefaultDescription = value;
  }

  @action
  setLedgerItemDefaultDescriptionViolations(value) {
    this.ledgerItemDefaultDescriptionViolations = value;
  }

  @action
  setLedgerItemIsDonation(value) {
    this.ledgerItemIsDonation = value;
  }

  @action
  setLedgerItemIsRefundable(value) {
    this.ledgerItemIsRefundable = value;
  }

  @action
  setLedgerItemIsTaxable(value) {
    this.ledgerItemIsTaxable = value;
  }

  @action
  setLedgerItemName(value) {
    this.ledgerItemName = value;
  }

  @action
  setLedgerItemNameViolations(value) {
    this.ledgerItemNameViolations = value;
  }

  @action
  setSelectedCostCentre(value) {
    this.selectedCostCentre = value;
  }

  @action
  setSelectedInternalOrder(value) {
    this.selectedInternalOrder = value;
  }

  @action
  setYear(value) {
    this.year = value;
  }

  // Services
  @service
  features;

  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  Jurisdiction = Jurisdiction;
  NswSapSyncStatus = NswSapSyncStatus;
  @tracked currentYear = moment().year();
  @tracked year = moment().year();

  get nextYear() {
    return this.currentYear + 1;
  }

  @optionAlias('finance:nswDoe:sendToSap')
  nswSapSyncEnabled;

  @computed(
    'features.sapFinance',
    'nswSapSyncEnabled',
    'session.scopes.[]',
    'model.items.@each.reference',
  )
  get nswSapSyncAvailable() {
    if (!this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // User doesn't have permission - only Hero staff can do this.
      return false;
    }
    if (!this.features.sapFinance || !this.nswSapSyncEnabled) {
      // SAP Finance not enabled (i.e. not an NSW school), or sending to SAP
      // is not enabled.
      return false;
    }

    // Allow sync if any Ledger Item is lacking a (Hero-generated) FeeValueId.
    return this.model.items.some((ledgerItem) => {
      return (
        isEmpty(ledgerItem.reference) || !ledgerItem.reference.startsWith('H')
      );
    });
  }

  @tracked
  nswSapSyncInProgress = false;

  @computed('model.groups.[]')
  get filteredGroups() {
    return this.model.groups
      .filter((group) => {
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!staff.find(
            (item) => item.personId === this.session.personId,
          );
        }
        if (group.status === NSWStatus.INACTIVE) {
          return false;
        }
        // Include all other groups.
        return true;
      })
      .sort(byKeys('type', 'label'));
  }

  @computed('model.ledgers.[]', 'features.sapFinance', 'ledgerItemId')
  get availableLedgers() {
    if (!this.features.sapFinance || !isEmpty(this.ledgerItemId)) {
      // For most schools, simply filter out hidden Ledgers.
      // The same goes if editing an existing Ledger.
      return this.model.ledgers.filter((item) => !item.isHidden);
    }

    // For SAP schools, filter out hidden and inactive Ledgers.
    return this.model.ledgers.filter(
      (ledger) => ledger.isActive && !ledger.isHidden,
    );
  }

  @filterBy('model.costCentres', 'isActive')
  availableCostCentres;

  @filterBy('model.internalOrders', 'isActive')
  availableInternalOrders;

  @tracked
  selectedCostCentre = null;
  @tracked
  selectedInternalOrder = null;
  @tracked
  selectedLedger = null;
  costCentreId = null;
  internalOrderId = null;
  ledgerId = null;
  @tracked ledgerItemId = null;
  @tracked
  ledgerItemName = '';
  @tracked
  ledgerItemDefaultDescription = '';
  @tracked
  ledgerItemDefaultDate = '';
  @tracked
  ledgerItemDefaultAmount = null;
  @tracked
  ledgerItemIsTaxable = true;
  @tracked
  ledgerItemIsDonation = false;
  @tracked
  ledgerItemIsRefundable = true;
  @tracked
  ledgerItemMinDefaultDate = '';
  @tracked
  ledgerItemMaxDefaultDate = '';
  @tracked
  selectedLedgerItem = null;
  @tracked
  itemDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked
  deleteItemDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  errorMessage = '';
  @tracked
  ledgerItemNameViolations = [];
  @tracked
  ledgerItemDefaultDescriptionViolations = [];

  @tracked
  ledgerItemLoaded;

  @tracked
  lineItemCount;

  @computed(
    'ledgerItemNameViolations.[]',
    'ledgerItemDefaultDescriptionViolations.[]',
  )
  get hasLanguageViolations() {
    return (
      !isEmpty(this.ledgerItemNameViolations) ||
      !isEmpty(this.ledgerItemDefaultDescriptionViolations)
    );
  }

  init() {
    super.init();
    this.resetForm();
  }

  @action
  syncToNswSap() {
    this.nswSapSyncInProgress = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/nsw/sap/feemappings/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.toaster.success(
          `Fee Mappings are being synced to SAP in the background.`,
        );
      })
      .catch(() => {
        this.toaster.error(`Error syncing Fee Mappings to SAP!`);
      })
      .finally(() => {
        this.nswSapSyncInProgress = false;
      });
  }

  @action
  editItem(id) {
    this.store.findRecord('finance/ledger-item', id).then(async (item) => {
      const relationships = await loadRelationships(item);
      this.selectedCostCentre = relationships.costCentre;
      this.selectedInternalOrder = relationships.internalOrder;
      this.selectedLedger = relationships.ledger;
      this.ledgerItemName = item.get('name');
      this.ledgerItemDefaultDescription = item.get('defaultDescription');
      this.ledgerItemDefaultDate = item.get('defaultDate');
      this.ledgerItemDefaultAmount = Number(item.get('defaultAmount')).toFixed(
        2,
      );
      this.ledgerItemIsTaxable = item.get('isTaxable');
      this.ledgerItemIsDonation = item.get('isDonation');
      this.ledgerItemIsRefundable = item.get('isRefundable');
      this.ledgerItemMinDefaultDate = `${this.year}-01-01`;
      this.ledgerItemMaxDefaultDate = `${this.year}-12-31`;
    });
    this.ledgerItemId = id;
    this.toggleItemDialog();
  }

  @action
  selectLedger(ledger) {
    this.selectedLedger = ledger;
    this.selectedInternalOrder = null;
  }

  @action
  saveItem() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';
    // taxType will only be set (below) for NSW/SAP schools.
    let taxType;
    let isTaxable = this.ledgerItemIsTaxable;
    let isDonation = this.ledgerItemIsDonation;
    if (this.features.sapFinance) {
      taxType = this.get('selectedLedger.taxType');
      isTaxable = taxType === AuTaxType.S1;
      isDonation = this.get('selectedLedger.isDeductibleGiftReceipt');
    }

    if (isEmpty(this.ledgerItemId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('finance/ledger-item', {
          costCentreId: this.selectedCostCentre,
          internalOrderId: this.selectedInternalOrder,
          ledgerId: this.selectedLedger,
          name: this.ledgerItemName,
          defaultDescription: this.ledgerItemDefaultDescription,
          defaultDate: this.ledgerItemDefaultDate,
          defaultAmount: this.ledgerItemDefaultAmount,
          isTaxable,
          isDonation,
          isRefundable: this.ledgerItemIsRefundable,
          taxType,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.ledgerItemId;
        this.store.findRecord('finance/ledger-item', id).then((ledger) => {
          ledger.setProperties({
            costCentreId: this.selectedCostCentre,
            internalOrderId: this.selectedInternalOrder,
            name: this.ledgerItemName,
            defaultDescription: this.ledgerItemDefaultDescription,
            defaultDate: this.ledgerItemDefaultDate,
            defaultAmount: this.ledgerItemDefaultAmount,
            isTaxable,
            isDonation,
            isRefundable: this.ledgerItemIsRefundable,
            taxType,
          });

          resolve(ledger);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('admin.finance.ledger-items.index');
          }

          this.itemDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          // The form still holds the edits for a retry; rollbackAttributes()
          // alone would leave the cost centre and internal order.
          rollbackRecord(record);
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteItem(id) {
    this.errorMessage = '';
    this.ledgerItemLoaded = false;
    hash({
      ledgerItem: this.store.findRecord('finance/ledger-item', id),
      lineItems: this.store.query('finance/line-item', { ledgerItemId: id }),
    }).then((data) => {
      this.ledgerItemName = data.ledgerItem.get('name');
      this.lineItemCount = data.lineItems.length;
      this.ledgerItemLoaded = true;
    });
    this.ledgerItemId = id;
    this.deleteItemDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let record = this.store.peekRecord(
          'finance/ledger-item',
          this.ledgerItemId,
        );

        // Delete record (locally + pushed to server).
        return record
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            record.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteItemDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  toggleApplyItemDialog(item) {
    this.errorMessage = '';
    if (item) {
      this.selectedLedgerItem = item;
    }
    this.toggleProperty('applyItemDialogOpen');
    if (!this.applyItemDialogOpen) {
      this.selectedLedgerItem.reload();
      this.resetApplyForm();
    }
  }

  @action
  async copyItem(item, targetYear) {
    let targetDate = moment(item.defaultDate, 'YYYY-MM-DD')
      .year(targetYear)
      .format('YYYY-MM-DD');
    const relationships = await loadRelationships(item);
    this.selectedCostCentre = relationships.costCentre;
    this.selectedInternalOrder = relationships.internalOrder;
    this.selectedLedger = relationships.ledger;
    this.ledgerItemName = item.get('name');
    this.ledgerItemDefaultDescription = item.get('defaultDescription');
    this.ledgerItemDefaultDate = targetDate;
    this.ledgerItemDefaultAmount = Number(item.get('defaultAmount')).toFixed(2);
    this.ledgerItemIsTaxable = item.get('isTaxable');
    this.ledgerItemIsDonation = item.get('isDonation');
    this.ledgerItemIsRefundable = item.get('isRefundable');
    this.ledgerItemMinDefaultDate = `${targetYear}-01-01`;
    this.ledgerItemMaxDefaultDate = `${targetYear}-12-31`;
    this.toggleItemDialog();
  }

  @action
  toggleItemDialog() {
    this.errorMessage = '';
    this.toggleProperty('itemDialogOpen');
    if (!this.itemDialogOpen) {
      this.resetForm();
    }
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    this.selectedCostCentre = null;
    this.selectedInternalOrder = null;
    this.selectedLedger = null;
    this.ledgerItemId = null;
    this.ledgerItemName = '';
    this.ledgerItemDefaultDescription = '';
    this.ledgerItemDefaultDate = '';
    this.ledgerItemDefaultAmount = null;
    this.ledgerItemIsTaxable = true;
    this.ledgerItemIsDonation = false;
    this.ledgerItemIsRefundable = true;
    this.ledgerItemMinDefaultDate = `${this.currentYear}-01-01`;
    this.ledgerItemMaxDefaultDate = `${this.nextYear}-12-31`;
    this.ledgerItemNameViolations = [];
    this.ledgerItemDefaultDescriptionViolations = [];
  }

  resetApplyForm() {
    this.selectedLedgerItem = null;
  }
}

// loadRelationships gives the ledger item's cost centre, internal order and
// ledger as records.
function loadRelationships(item) {
  // A belongsTo takes a record, not the async proxy.
  return hash({
    costCentre: item.costCentre,
    internalOrder: item.internalOrder,
    ledger: item.ledger,
  });
}
